// SPDX-License-Identifier: Apache-2.0

// TempleSummonGate — lifecycle wrapper around the temple iframe.
//
// On mount: open a WS to the local MCP server at ws://localhost:5417/ws and
// call the `summon_temple` tool. The tool spawns the ora-relay (port 8788)
// and the static eval server (port 8000) as subprocesses, then reports
// `status: "ready" | "already-running" | "failed"`.
//
// While the call is in flight we render a tasteful pulsing star (the
// temple's signature face glyph), not a generic spinner. On `ready` we
// hand the iframe URL to the parent route via `onReady`. On `failed` we
// show the diagnostic and a retry button. If the failure is the
// "claude binary not found" class, we surface a dedicated install message
// instead of a cryptic stack trace.
//
// On unmount: call `dismiss_temple` ONLY if THIS gate was the one that
// summoned the temple. If the temple was already running before we hit
// the route (status === "already-running"), leave it for whoever owns
// the lifecycle elsewhere.
//
// Idempotent: the MCP tool itself is responsible for "already running →
// no-op" semantics. We just trust its `status` field.

import { Show, createSignal, onCleanup, onMount } from "solid-js";
import { getActiveWorkspaceIdHeader } from "../../lib/api";

const MCP_WS_URL = "ws://localhost:5417/ws";

type SummonState =
  | { kind: "starting" }
  | { kind: "ready"; iframeUrl: string }
  | { kind: "failed"; message: string; needsClaudeInstall: boolean };

interface SummonResult {
  status: "ready" | "already-running" | "failed";
  iframeUrl?: string;
  relayPort?: number;
  staticPort?: number;
  message?: string;
}

interface ToolResultEnvelope {
  status?: SummonResult["status"];
  iframeUrl?: string;
  relayPort?: number;
  staticPort?: number;
  message?: string;
  content?: Array<{ type: string; text?: string }>;
}

interface JsonRpcResponse {
  jsonrpc: "2.0";
  id: number;
  result?: ToolResultEnvelope;
  error?: { code: number; message: string };
}

const DEFAULT_IFRAME_URL =
  "http://localhost:8000/eval/harness-temple.html?relay=ws://localhost:8788";

function detectClaudeMissing(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes("claude binary") ||
    m.includes("claude not found") ||
    m.includes("claude code max") ||
    m.includes("enoent") && m.includes("claude")
  );
}

interface Props {
  /** Render the iframe once the temple is ready. */
  children: (iframeUrl: string) => unknown;
}

export default function TempleSummonGate(props: Props) {
  const [state, setState] = createSignal<SummonState>({ kind: "starting" });
  // Did THIS mount summon the temple? If false, we leave it alone on unmount.
  let weSummoned = false;
  let socket: WebSocket | null = null;
  let mcpReqId = 1;
  let cancelled = false;

  function openSocket(): Promise<WebSocket> {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(MCP_WS_URL);
      const timer = setTimeout(() => {
        ws.close();
        reject(new Error("MCP server unreachable at " + MCP_WS_URL));
      }, 4000);
      ws.addEventListener(
        "open",
        () => {
          clearTimeout(timer);
          // Bind the active workspace to this WS connection so summon_temple
          // (and any subsequent tool calls on this socket) execute against
          // the right vault.
          try {
            ws.send(
              JSON.stringify({
                type: "hello",
                workspaceId: getActiveWorkspaceIdHeader(),
              }),
            );
          } catch {
            /* socket might already be in a torn state — let the caller surface */
          }
          resolve(ws);
        },
        { once: true },
      );
      ws.addEventListener(
        "error",
        () => {
          clearTimeout(timer);
          reject(new Error("MCP server connection error"));
        },
        { once: true },
      );
    });
  }

  function callTool(ws: WebSocket, name: string, args: Record<string, unknown> = {}): Promise<SummonResult> {
    return new Promise((resolve, reject) => {
      const id = mcpReqId++;
      const onMessage = (ev: MessageEvent) => {
        let parsed: JsonRpcResponse | { type?: string };
        try {
          parsed = JSON.parse(typeof ev.data === "string" ? ev.data : "");
        } catch {
          return;
        }
        // Skip the server hello frame and approval frames.
        if ("type" in parsed && parsed.type) return;
        const r = parsed as JsonRpcResponse;
        if (r.id !== id) return;
        ws.removeEventListener("message", onMessage);
        if (r.error) {
          reject(new Error(r.error.message));
          return;
        }
        // Tool results come back via MCP's content[] envelope. The summon_temple
        // tool returns either a structured object directly or wraps JSON in
        // content[0].text. Handle both shapes defensively.
        const result: ToolResultEnvelope = r.result ?? {};
        if (result.status) {
          resolve({
            status: result.status,
            iframeUrl: result.iframeUrl,
            relayPort: result.relayPort,
            staticPort: result.staticPort,
            message: result.message,
          });
          return;
        }
        const text = result.content?.[0]?.text;
        if (text) {
          try {
            resolve(JSON.parse(text) as SummonResult);
            return;
          } catch {
            resolve({ status: "ready", message: text });
            return;
          }
        }
        resolve({ status: "ready" });
      };
      ws.addEventListener("message", onMessage);
      ws.send(
        JSON.stringify({
          jsonrpc: "2.0",
          id,
          method: "tools/call",
          params: { name, arguments: args },
        }),
      );
    });
  }

  async function summon() {
    setState({ kind: "starting" });
    try {
      socket = await openSocket();
      const result = await callTool(socket, "summon_temple", {});
      if (cancelled) return;
      if (result.status === "failed") {
        const msg = result.message ?? "summon_temple returned failed";
        setState({
          kind: "failed",
          message: msg,
          needsClaudeInstall: detectClaudeMissing(msg),
        });
        return;
      }
      weSummoned = result.status === "ready";
      const url = result.iframeUrl ?? DEFAULT_IFRAME_URL;
      setState({ kind: "ready", iframeUrl: url });
    } catch (err) {
      if (cancelled) return;
      const msg = err instanceof Error ? err.message : String(err);
      setState({
        kind: "failed",
        message: msg,
        needsClaudeInstall: detectClaudeMissing(msg),
      });
    }
  }

  async function dismiss() {
    if (!weSummoned) return;
    try {
      const ws = socket && socket.readyState === WebSocket.OPEN ? socket : await openSocket();
      await callTool(ws, "dismiss_temple", {});
    } catch {
      // Best-effort — if MCP is gone, the subprocesses may already be dead.
    }
  }

  onMount(() => {
    void summon();
  });

  onCleanup(() => {
    cancelled = true;
    void dismiss().finally(() => {
      try {
        socket?.close();
      } catch {
        /* noop */
      }
    });
  });

  return (
    <Show
      when={(() => {
        const s = state();
        return s.kind === "ready" ? s : null;
      })()}
      fallback={<GateUI state={state()} onRetry={() => void summon()} />}
    >
      {(ready) => <>{props.children(ready().iframeUrl)}</>}
    </Show>
  );
}

function GateUI(props: { state: SummonState; onRetry: () => void }) {
  return (
    <div class="temple-gate" data-testid="temple-gate" data-state={props.state.kind}>
      <div class="temple-gate__card">
        <Show
          when={props.state.kind === "starting"}
          fallback={
            <div class="temple-star temple-star--failed" aria-hidden="true">
              <span class="temple-star__glyph">·</span>
            </div>
          }
        >
          <div class="temple-star" aria-hidden="true">
            <span class="temple-star__glyph">✦</span>
          </div>
        </Show>

        <Show when={props.state.kind === "starting"}>
          <h2 class="temple-gate__heading">opening the temple…</h2>
          <p class="temple-gate__sub">
            spawning the relay and the embodied loop. this takes a moment the
            first time.
          </p>
        </Show>

        <Show when={props.state.kind === "failed"}>
          {(() => {
            const s = props.state as Extract<SummonState, { kind: "failed" }>;
            return (
              <>
                <h2 class="temple-gate__heading">the temple did not open</h2>
                <Show
                  when={s.needsClaudeInstall}
                  fallback={
                    <>
                      <p class="temple-gate__sub">
                        the summon tool returned an error. check that the MCP
                        server is running on port 5417 and try again.
                      </p>
                      <pre class="temple-gate__diag">{s.message}</pre>
                    </>
                  }
                >
                  <p class="temple-gate__sub">
                    the embodied chat needs a Claude Code Max subscription so a
                    local <code>claude</code> binary can drive the face.
                  </p>
                  <div class="temple-gate__install-hint">
                    install Claude Code, then sign in:
                    {"\n"}
                    <code>npm install -g @anthropic-ai/claude-code</code>
                    {"\n"}
                    <code>claude /login</code>
                  </div>
                </Show>
                <button
                  class="temple-gate__retry"
                  type="button"
                  onClick={props.onRetry}
                  data-testid="temple-gate-retry"
                >
                  retry
                </button>
              </>
            );
          })()}
        </Show>
      </div>
    </div>
  );
}
